import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { type ComponentProps, type ReactNode, type WheelEvent } from 'react';
import { portaledSurfaceVariants, type PortaledSurfaceVariant } from '../lib/portaledSurface';
import { cn } from '../lib/utils';
import { ComboboxList as UiComboboxList } from './ui/combobox';

/** Dialog RemoveScroll cancels wheel on body-portaled menus; apply the delta ourselves. */
function applyWheelToNearestScrollable(event: WheelEvent<HTMLElement>) {
  const root = event.currentTarget;
  let node: HTMLElement | null =
    event.target instanceof HTMLElement
      ? event.target
      : event.target instanceof Node
        ? event.target.parentElement
        : null;

  while (node && root.contains(node)) {
    const { overflowY } = getComputedStyle(node);
    const canScrollY =
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      node.scrollHeight > node.clientHeight;
    if (canScrollY) {
      node.scrollTop += event.deltaY;
      event.stopPropagation();
      return;
    }
    node = node.parentElement;
  }
}

type DialogComboboxListProps<G> = Omit<ComponentProps<typeof UiComboboxList>, 'children'> & {
  children?: ReactNode | ((group: G, index: number) => ReactNode);
};

export function DialogComboboxList<G>({ className, ...props }: DialogComboboxListProps<G>) {
  return (
    <UiComboboxList
      {...(props as ComponentProps<typeof UiComboboxList>)}
      className={cn('min-h-0 overflow-y-auto', className)}
    />
  );
}

type DialogComboboxContentProps = ComboboxPrimitive.Popup.Props &
  Pick<
    ComboboxPrimitive.Positioner.Props,
    'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor'
  > & {
    /** Light (default) or dark surface for portaled menus on a dark page. */
    variant?: PortaledSurfaceVariant;
  };

/**
 * Combobox menu for use inside Dialog.
 * Portals above the dialog, takes clicks, and forwards wheel to the list.
 */
export function DialogComboboxContent({
  className,
  variant = 'light',
  side = 'bottom',
  sideOffset = 6,
  align = 'start',
  alignOffset = 0,
  anchor,
  onPointerDown,
  onWheel,
  ...props
}: DialogComboboxContentProps) {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="tw pointer-events-auto isolate z-[300]"
      >
        <ComboboxPrimitive.Popup
          data-slot="dialog-combobox-content"
          data-chips={!!anchor}
          className={cn(
            'group/combobox-content w-(--anchor-width) max-w-(--available-width) origin-(--transform-origin) tw pointer-events-auto relative z-[300] flex max-h-96 min-w-[calc(var(--anchor-width)+--spacing(7))] flex-col overflow-hidden rounded-md shadow-md ring-1 ring-foreground/10 duration-100',
            portaledSurfaceVariants({ variant }),
            'data-[chips=true]:min-w-(--anchor-width) data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            className,
          )}
          onPointerDown={(event) => {
            event.stopPropagation();
            onPointerDown?.(event);
          }}
          onWheel={(event) => {
            applyWheelToNearestScrollable(event);
            onWheel?.(event);
          }}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}
